import { jsPDF } from 'jspdf';
import SellytLogoSvg from '@/assets/svg/sellyt-logo.svg?url';

export interface BillingReceiptPdfInput {
  invoiceId: string;
  plan: string;
  amount: string;
  currency: string;
  paymentMethod: string;
  status: string;
  startDate: string;
  endDate: string;
  autoRenew: string;
  paidAt?: string | null;
}

const getOrdinal = (n: number) => {
  const s = ['th', 'st', 'nd', 'rd'];
  const v = n % 100;
  return n + (s[(v - 20) % 10] ?? s[v] ?? s[0]);
};

const formatReceiptDate = (dateStr: string | null | undefined) => {
  if (!dateStr) return '—';
  const d = new Date(dateStr);
  if (Number.isNaN(d.getTime())) return dateStr;
  const day = d.getDate();
  const month = d.toLocaleDateString('en-GB', { month: 'long' });
  const year = d.getFullYear();
  return `${getOrdinal(day)} ${month} ${year}`;
};

const formatMoneyPdf = (amount: string, currency: string) => {
  const num = parseFloat(amount);
  if (Number.isNaN(num)) return '—';
  const code = currency && currency !== 'NGN' ? currency : 'NGN';
  return `${code} ${num.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
};

const drawDottedLine = (
  doc: jsPDF,
  y: number,
  margin: number,
  width: number
) => {
  const dashLen = 2;
  const gapLen = 2;
  doc.setDrawColor(180, 180, 180);
  let x = margin;
  while (x < margin + width) {
    const x2 = Math.min(x + dashLen, margin + width);
    doc.line(x, y, x2, y);
    x = x2 + gapLen;
  }
};

const loadLogoDataUrl = (): Promise<string | null> =>
  new Promise((resolve) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      try {
        const canvas = document.createElement('canvas');
        canvas.width = img.naturalWidth;
        canvas.height = img.naturalHeight;
        const ctx = canvas.getContext('2d');
        if (!ctx) {
          resolve(null);
          return;
        }
        ctx.drawImage(img, 0, 0);
        resolve(canvas.toDataURL('image/png'));
      } catch {
        resolve(null);
      }
    };
    img.onerror = () => resolve(null);
    img.src = SellytLogoSvg;
  });

export const downloadBillingReceiptPdf = async (
  item: BillingReceiptPdfInput
) => {
  const logoDataUrl = await loadLogoDataUrl();
  const doc = new jsPDF({ format: 'a4', unit: 'mm' });
  const margin = 20;
  const pageWidth = 210 - margin * 2;
  let y = 20;
  const lineHeight = 6;
  const labelColumnWidth = 42;

  const addLine = (
    text: string,
    opts?: { fontSize?: number; bold?: boolean; align?: 'left' | 'center' }
  ) => {
    doc.setFontSize(opts?.fontSize ?? 10);
    doc.setFont('helvetica', opts?.bold ? 'bold' : 'normal');
    const align = opts?.align ?? 'left';
    const x = align === 'center' ? margin + pageWidth / 2 : margin;
    doc.text(text, x, y, { align: align === 'center' ? 'center' : 'left' });
    y += opts?.fontSize ? opts.fontSize * 0.5 + 2 : lineHeight;
  };

  const addRow = (label: string, value: string) => {
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.text(label, margin, y);
    doc.text(value, margin + pageWidth - 2, y, {
      align: 'right',
      maxWidth: pageWidth - labelColumnWidth - 2,
    });
    y += lineHeight;
  };

  const logoHeight = 12;
  const logoWidth = 32;
  if (logoDataUrl) {
    try {
      doc.addImage(
        logoDataUrl,
        'PNG',
        margin + (pageWidth - logoWidth) / 2,
        y,
        logoWidth,
        logoHeight
      );
      y += logoHeight + 4;
    } catch {
      doc.setTextColor(34, 197, 94);
      addLine('Sellyt', { fontSize: 18, bold: true, align: 'center' });
      y += 2;
      doc.setTextColor(0, 0, 0);
    }
  } else {
    doc.setTextColor(34, 197, 94);
    addLine('Sellyt', { fontSize: 18, bold: true, align: 'center' });
    y += 2;
    doc.setTextColor(0, 0, 0);
  }

  drawDottedLine(doc, y, margin, pageWidth);
  y += 6;
  addLine('RECEIPT', { fontSize: 18, align: 'center' });
  drawDottedLine(doc, y, margin, pageWidth);
  y += 4;
  addLine('SELLYT SUBSCRIPTION', { fontSize: 18, align: 'center' });
  y += 2;
  drawDottedLine(doc, y, margin, pageWidth);
  y += 4;

  addRow('Receipt ID:', `#${item.invoiceId}`);
  addRow('Date:', formatReceiptDate(item.paidAt ?? item.startDate));
  y += 4;

  doc.setTextColor(34, 197, 94);
  addLine('Plan details', { bold: true });
  doc.setTextColor(0, 0, 0);
  drawDottedLine(doc, y, margin, pageWidth);
  y += 5;
  addRow('Plan:', item.plan);
  addRow('Start date:', formatReceiptDate(item.startDate));
  addRow('End date:', formatReceiptDate(item.endDate));
  addRow('Auto-renew:', item.autoRenew);
  addRow('Status:', item.status);
  y += 4;

  doc.setTextColor(34, 197, 94);
  addLine('Payment summary', { bold: true });
  doc.setTextColor(0, 0, 0);
  drawDottedLine(doc, y, margin, pageWidth);
  y += 5;
  addRow('Description:', item.plan);
  addRow('Qty:', '1');
  addRow('Price:', formatMoneyPdf(item.amount, item.currency));
  addRow('Subtotal:', formatMoneyPdf(item.amount, item.currency));
  y += 2;

  doc.setFillColor(240, 253, 244);
  doc.rect(margin, y - 2, pageWidth, 10, 'F');
  doc.setTextColor(34, 197, 94);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(10);
  doc.text('Total paid:', margin, y + 4);
  doc.setTextColor(0, 0, 0);
  doc.text(
    formatMoneyPdf(item.amount, item.currency),
    margin + pageWidth - 2,
    y + 4,
    {
      align: 'right',
      maxWidth: pageWidth - labelColumnWidth - 2,
    }
  );
  y += 12;

  doc.setTextColor(34, 197, 94);
  addLine('Payment method', { bold: true });
  doc.setTextColor(0, 0, 0);
  drawDottedLine(doc, y, margin, pageWidth);
  y += 5;
  addRow('Provider:', item.paymentMethod);
  y += 6;

  doc.setFontSize(8);
  doc.setTextColor(128, 128, 128);
  addLine('POWERED BY GOTHRIVE TECHNOLOGIES', { align: 'center' });

  doc.save(`receipt-${item.invoiceId}.pdf`);
};
